import React from 'react';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import ContactMailOutlined from '@mui/icons-material/ContactMailOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import PolicyOutlined from '@mui/icons-material/PolicyOutlined';
import PrivacyTipOutlined from '@mui/icons-material/PrivacyTipOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import EggOutlined from '@mui/icons-material/EggOutlined';
import { useNavigate } from 'react-router-dom';

import packageJson from '../../../package.json';
import { KNOW_YOUR_CHICKEN_PATH, SIDE_MENU_LEGAL_LINKS } from '../config/footerLinks';
import { paths } from '../config/paths';
import { v2Theme } from '../design/theme';
import { useCartItemCount } from '../hooks/useCartItemCount';

const APP_VERSION = process.env.REACT_APP_VERSION || packageJson.version;

const LEGAL_LINK_ICONS = {
  [paths.contactUs]: ContactMailOutlined,
  [paths.termsAndConditions]: ArticleOutlined,
  [paths.privacyPolicy]: PrivacyTipOutlined,
  [paths.refundPolicy]: ReceiptLongOutlined,
};

export default function SideMenu({ open, onClose }) {
  const navigate = useNavigate();
  const cartItemCount = useCartItemCount();

  const go = (to) => {
    navigate(to);
    onClose();
  };

  const cartIcon = (
    <Badge
      color="error"
      max={99}
      badgeContent={cartItemCount}
      invisible={cartItemCount <= 0}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: 10,
          height: 18,
          minWidth: 18,
        },
      }}
    >
      <ShoppingBagOutlined />
    </Badge>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0,0,0,0.35)' },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'min(300px, 88vw)', sm: 300 },
          bgcolor: v2Theme.palette.background,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          pt: 'calc(8px + env(safe-area-inset-top, 0px))',
          pb: 'env(safe-area-inset-bottom, 0px)',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Menu
        </Typography>
        <IconButton aria-label="Close menu" onClick={onClose} edge="end" size="large">
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ py: 1, px: 0.5 }} disablePadding>
        <ListItemButton onClick={() => go(paths.home)} sx={{ borderRadius: 1, py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>
            <HomeOutlined />
          </ListItemIcon>
          <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton onClick={() => go(paths.cart)} sx={{ borderRadius: 1, py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>{cartIcon}</ListItemIcon>
          <ListItemText primary="Cart" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton onClick={() => go(KNOW_YOUR_CHICKEN_PATH)} sx={{ borderRadius: 1, py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>
            <EggOutlined />
          </ListItemIcon>
          <ListItemText primary="Know your chicken" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton onClick={() => go(paths.stores)} sx={{ borderRadius: 1, py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>
            <StorefrontOutlined />
          </ListItemIcon>
          <ListItemText primary="Our Stores" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton onClick={() => go(paths.account)} sx={{ borderRadius: 1, py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>
            <PersonOutlineOutlined />
          </ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
      </List>
      <Divider sx={{ mx: 1.5 }} />
      <List sx={{ py: 1, px: 0.5 }} disablePadding>
        {SIDE_MENU_LEGAL_LINKS.map(({ label, path }) => {
          const Icon = LEGAL_LINK_ICONS[path] || PolicyOutlined;
          return (
            <ListItemButton key={path} onClick={() => go(path)} sx={{ borderRadius: 1, py: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 44, color: 'text.primary' }}>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', px: 2, py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          v{APP_VERSION}
        </Typography>
      </Box>
    </Drawer>
  );
}
